import React from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Formik } from 'formik'
import { Checkbox, TextField } from 'src/Libs/inputs/formik'
import { Stack } from '@mui/system'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { createCategory, updateCategory } from '../../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { DownloadsCategoryModel, DownloadsParent } from '../../models'
import SubcategoryHint from './SubcategoryHint'
import SubcategoryRestrictions from './SubcategoryRestrictions'
import useModals from 'src/Hooks/useModals'
import { Box, Typography } from '@mui/material'
import { useCategoryEditorFormInitializer, useCategoryDisplayName } from '../../hooks'

export interface CategoryEditorProps {
  parent?: DownloadsParent
  item?: DownloadsCategoryModel
  id?: number
  onClose: () => void
  isFirstLevel?: boolean
  selectCategory: (categoryId?: number) => void
}

export interface CreateCategoryFormValues {
  path: string | null
  nameDe: string
  nameEn: string
  nameFr: string
  nameNl: string
  nameXx?: string
  visibleToCustomer: boolean
  visibleToInstaller: boolean
  isFirstLevel?: boolean
}
export type CreateCategoryForm = Omit<CreateCategoryFormValues, 'path'>

const CategoryEditor: React.FC<CategoryEditorProps> = ({ item, parent, onClose, isFirstLevel, selectCategory }) => {
  const { t } = useCustomTranslation()
  const getCategoryDisplayName = useCategoryDisplayName()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { openDeleteCategoryDialog, openMoveDownloadItemDialog, openUploadFileDialog, openConfirmationModal } = useModals()
  const dispatch = useDispatch()
  const parentRestrictsUserLevels = parent && (!parent.visibleToCustomer || !parent.visibleToInstaller)
  const isEditing = !!item

  const { initialValues, validationSchema } = useCategoryEditorFormInitializer(item, parent)

  const onUpdateCategory = (values: CreateCategoryFormValues): void => {
    if (item) {
      const { path, ...payload } = values
      updateCategory(item.id, payload, authFetch)
        .then(result => {
          onClose?.()
          dispatch(refreshApiView())
          enqueueSuccessStack(t('documents.edit-category.success'))
        })
        .catch(error => {
          enqueueErrorStack(t('documents.edit-category.error'))
          console.error('Error updating category:', error)
        })
        .finally(() => {
          const { ...filteredValues } = values
          const newItem = { ...item, ...filteredValues }
          selectCategory(newItem.id)
        })
    }
  }

  const onSubmit = (values: CreateCategoryFormValues): void => {
    const havePermissionsBeenReduced =
      (initialValues.visibleToCustomer && !values.visibleToCustomer) || (initialValues.visibleToInstaller && !values.visibleToInstaller)
    const needsConfimationModal = havePermissionsBeenReduced

    if (item && needsConfimationModal) {
      openConfirmationModal({
        title: t('documents.edit-category.warning.title'),
        text: t('documents.edit-category.warning.text'),
        buttonText: [t('common.cancel'), t('common.save')],
        onConfirm: () => {
          onUpdateCategory(values)
          onClose?.()
        },
      })
      return
    } else if (item && !needsConfimationModal) {
      onUpdateCategory(values)
      return
    }

    createCategory(values, authFetch)
      .then(result => {
        onClose?.()
        dispatch(refreshApiView())
        enqueueSuccessStack(t('documents.add-category.success'))
        selectCategory(parent?.id)
      })
      .catch(error => {
        enqueueErrorStack(t('documents.add-category.error'))
        console.error('Error creating category:', error)
      })
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={isEditing ? t('documents.edit-category.title') : parent ? t('documents.add-subcategory.title') : t('documents.add-category.title')}
            maxWidth="xs"
            onClose={onClose}
            actions={{
              primary: {
                name: isEditing ? t('common.save') : t('common.create'),
                onClick: handleSubmit,
                disabled: !isValid,
              },
              secondary: [
                {
                  name: t('common.cancel'),
                  onClick: onClose,
                },
              ],
            }}
            stackActions={
              item
                ? [
                    {
                      name: t('documents.edit-category.actions.move'),
                      onClick: () => {
                        onClose()
                        openMoveDownloadItemDialog({ itemToMoveId: item.id, selectCategory })
                      },
                      dataQa: 'button-move-category',
                    },
                    {
                      name: t('common.delete'),
                      onClick: () => openDeleteCategoryDialog({ category: item, parent, closeEditModal: onClose, onClose, selectCategory }),
                      dataQa: 'button-remove-category',
                    },
                    {
                      name: t('documents.upload.button'),
                      onClick: () => openUploadFileDialog({ parent: item, closeEditModal: onClose, onClose }),
                      dataQa: 'button-upload-file',
                    },
                  ]
                : undefined
            }
          >
            <Stack direction={'column'} spacing={2} sx={{ mt: 2 }}>
              {!item && parent && <SubcategoryHint parentName={getCategoryDisplayName(parent.nameLanguages)} />}
              <TextField name="nameDe" label={t('documents.add-category.form.name-de')} required fullWidth dataQa={'input-category-name-german'} />
              <TextField name="nameEn" label={t('documents.add-category.form.name-en')} required fullWidth dataQa={'input-category-name-english'} />
              <TextField name="nameFr" label={t('documents.add-category.form.name-fr')} required fullWidth dataQa={'input-category-name-french'} />
              <TextField name="nameNl" label={t('documents.add-category.form.name-nl')} required fullWidth dataQa={'input-category-name-dutch'} />
              <TextField name="nameXx" label={t('documents.add-category.form.name-xx')} fullWidth dataQa={'input-category-name-other'} />
              <Box>
                <Typography variant="caption" sx={{ pl: 1 }}>
                  {t('documents.add-category.form.checkbox-label')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Checkbox
                    name="visibleToCustomer"
                    label={t('documents.add-category.form.visible-to-customer')}
                    disabled={parent ? !parent.visibleToCustomer : false}
                    dataQa={'checkbox-visible-to-customer'}
                  />
                  <Checkbox
                    name="visibleToInstaller"
                    label={t('documents.add-category.form.visible-to-installer')}
                    disabled={parent ? !parent.visibleToInstaller : false}
                    dataQa={'checkbox-visible-to-customer'}
                  />
                  <Checkbox
                    name="visibleToService"
                    label={t('documents.add-category.form.visible-to-service')}
                    checked={true}
                    disabled
                    dataQa={'checkbox-visible-to-customer'}
                  />
                </Stack>
              </Box>
              {parentRestrictsUserLevels && (
                <SubcategoryRestrictions
                  parentVisibleToCustomer={!!parent?.visibleToCustomer}
                  parentVisibleToInstaller={!!parent?.visibleToInstaller}
                />
              )}
            </Stack>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default CategoryEditor
